<template>
    <div v-on-clickaway="closeSearch" class="relative z-50 w-full max-w-xs">
        <div class="relative">
            <input
                v-model="searchTerm"
                ref="input"
                type="text"
                class="rounded-full border shadow py-2 px-4 w-full"
                placeholder="Zoeken..."
                @input.stop="search"
                @keydown.stop=""
                @keydown.esc.stop="closeSearch"
                @focus="openSearch"
            >

            <!-- Loader -->
            <div
                v-if="isLoading"
                class="bg-white py-3 overflow-hidden absolute rounded-lg
                shadow-lg w-full mt-2 max-h-search overflow-y-auto"
            >
                <svg
                    class="mx-auto block"
                    style="width: 40px;"
                    viewBox="0 0 120 30"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                >
                    <circle cx="15" cy="15" r="15">
                        <animate
                            attributeName="r"
                            from="15"
                            to="15"
                            begin="0s"
                            dur="0.8s"
                            values="15;9;15"
                            calcMode="linear"
                            repeatCount="indefinite"
                        />
                        <animate
                            attributeName="fill-opacity"
                            from="1"
                            to="1"
                            begin="0s"
                            dur="0.8s"
                            values="1;.5;1"
                            calcMode="linear"
                            repeatCount="indefinite"
                        />
                    </circle>
                    <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                        <animate
                            attributeName="r"
                            from="9"
                            to="9"
                            begin="0s"
                            dur="0.8s"
                            values="9;15;9"
                            calcMode="linear"
                            repeatCount="indefinite"
                        />
                        <animate
                            attributeName="fill-opacity"
                            from="0.5"
                            to="0.5"
                            begin="0s"
                            dur="0.8s"
                            values=".5;1;.5"
                            calcMode="linear"
                            repeatCount="indefinite"
                        />
                    </circle>
                    <circle cx="105" cy="15" r="15">
                        <animate
                            attributeName="r"
                            from="15"
                            to="15"
                            begin="0s"
                            dur="0.8s"
                            values="15;9;15"
                            calcMode="linear"
                            repeatCount="indefinite"
                        />
                        <animate
                            attributeName="fill-opacity"
                            from="1"
                            to="1"
                            begin="0s"
                            dur="0.8s"
                            values="1;.5;1"
                            calcMode="linear"
                            repeatCount="indefinite"
                        />
                    </circle>
                </svg>
            </div>

            <!-- No Results Found -->
            <div
                v-if="shouldShowNoResults"
                class="bg-white overflow-hidden absolute rounded-lg shadow-lg
                w-full mt-2 max-h-search overflow-y-auto"
            >
                <h3 class="text-xs uppercase tracking-wide text-80 bg-40 py-4 px-3">
                    No Results Found.
                </h3>
            </div>

            <div
                v-if="shouldShowResults"
                class="bg-white overflow-hidden absolute rounded-lg shadow-lg
                w-full mt-2 max-h-search overflow-y-auto"
            >
                <div v-for="(group, index) in formattedResults" :key="index">
                    <h3 class="text-xs font-bold uppercase tracking-wide bg-gray-200 py-2 px-3">
                        {{ group.resourceTitle }}
                    </h3>
                    <ul class="list-reset">
                        <li
                            v-for="item in group.items"
                            :key="item.resourceName + ' ' + item.index"
                        >
                            <a
                                :href="item.link"
                                class="cursor-pointer flex items-center
                                hover:bg-20 block py-2 px-3 no-underline
                                font-normal bg-white"
                            >
                                <div>
                                    <p class="text-90">{{ item.title }}</p>
                                </div>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import _ from 'lodash';
    import { mixin as clickaway } from 'vue-clickaway';

    export default {
        name: 'Searchbar',
        data() {
            return {
                isLoading: false,
                searchTerm: '',
                currentlySearching: false,
                searchResults: [],
            };
        },
        mixins: [clickaway],
        methods: {
            search(event) {
                this.isLoading = true;

                if (this.searchTerm === '') {
                    this.isLoading = false;
                    this.searchResults = [];
                } else {
                    this.debouncer(() => {
                        this.fetchResults(event.target.value);
                    }, 500);
                }
            },

            async fetchResults(search) {
                this.searchResults = [];

                if (search !== '') {
                    try {
                        const { data } = await axios.get(this.route('api.search', { query: this.searchTerm }));

                        this.searchResults = data;
                        this.isLoading = false;
                    } catch (e) {
                        this.isLoading = false;
                        throw e;
                    }
                }
            },

            /**
             * Debounce function for the search handler
             */
            debouncer: _.debounce(callback => callback(), 500),

            openSearch() {
                this.clearSearch();
                this.$refs.input.focus();
                this.currentlySearching = true;
                this.clearResults();
            },

            closeSearch() {
                this.clearSearch();
                this.clearResults();
                this.$refs.input.blur();
                this.currentlySearching = false;
                this.isLoading = false;
            },

            clearSearch() {
                this.searchTerm = '';
            },

            clearResults() {
                this.results = [];
            },
        },
        computed: {
            hasResults() {
                return this.searchResults.length > 0;
            },

            hasSearchTerm() {
                return this.searchTerm !== '';
            },

            shouldShowNoResults() {
                return (
                    this.currentlySearching
                    && !this.isLoading
                    && !this.hasResults
                    && this.hasSearchTerm
                );
            },

            shouldShowResults() {
                return this.currentlySearching && this.hasResults && !this.isLoading;
            },

            indexedResults() {
                return _.map(this.searchResults, (item, index) => ({ index, ...item }));
            },

            formattedGroups() {
                return _.chain(this.indexedResults)
                    .map(item => ({
                        resourceName: item.resourceName,
                        resourceTitle: item.resourceTitle,
                    }))
                    .uniqBy('resourceName')
                    .value();
            },

            formattedResults() {
                return _.map(this.formattedGroups, group => ({
                    resourceName: group.resourceName,
                    resourceTitle: group.resourceTitle,
                    items: _.filter(
                        this.indexedResults,
                        item => item.resourceName === group.resourceName,
                    ),
                }));
            },
        },
        // watch: {
        //     query: {
        //         handler: debounce(function () { // eslint-disable-line
        //             this.searchProducts();
        //         }, 100),
        //     },
        // },
    };
</script>
